import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  IconButton,
  Chip,
  Avatar,
  Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';

interface RecruitmentManagementProps {
  farmId: string;
}

interface Candidate {
  id: string;
  name: string;
  email: string;
  phone: string;
  roleApplied: string;
  stage: 'applied' | 'screening' | 'interview' | 'offer' | 'hired' | 'rejected';
  interviewDate?: string;
  notes?: string;
}

const stages = [
  { id: 'applied', label: 'Applied', color: 'default' },
  { id: 'screening', label: 'Screening', color: 'info' },
  { id: 'interview', label: 'Interview', color: 'primary' },
  { id: 'offer', label: 'Offer', color: 'warning' },
  { id: 'hired', label: 'Hired', color: 'success' },
  { id: 'rejected', label: 'Rejected', color: 'error' }
];

const RecruitmentManagement: React.FC<RecruitmentManagementProps> = ({ farmId }) => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  const [newCandidate, setNewCandidate] = useState<Partial<Candidate>>({
    stage: 'applied'
  });

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('recruitment_candidates')
        .select('*')
        .eq('farm_id', farmId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mapped = (data || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        email: c.email,
        phone: c.phone,
        roleApplied: c.role_applied,
        stage: c.stage,
        interviewDate: c.interview_date,
        notes: c.notes
      }));

      setCandidates(mapped);
    } catch (err: any) {
      console.error('Error fetching candidates:', err);
      setError('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (farmId) fetchCandidates();
  }, [farmId]);

  const handleSubmit = async () => {
    try {
      if (!newCandidate.name || !newCandidate.roleApplied) {
        setError('Please fill in required fields');
        return;
      }

      const { error } = await supabase
        .from('recruitment_candidates')
        .insert([{
          farm_id: farmId,
          name: newCandidate.name,
          email: newCandidate.email,
          phone: newCandidate.phone,
          role_applied: newCandidate.roleApplied,
          stage: newCandidate.stage,
          interview_date: newCandidate.interviewDate,
          notes: newCandidate.notes
        }]);

      if (error) throw error;
      
      setOpenDialog(false);
      setNewCandidate({ stage: 'applied' });
      await fetchCandidates();
    } catch (err: any) {
      console.error('Error adding candidate:', err);
      setError('Failed to add candidate');
    }
  };

  const handleStageChange = async (id: string, newStage: string) => {
    try {
      const { error } = await supabase
        .from('recruitment_candidates')
        .update({ stage: newStage })
        .eq('id', id);

      if (error) throw error;
      await fetchCandidates();
    } catch (err) {
      console.error('Error updating stage:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('recruitment_candidates').delete().eq('id', id);
      if (error) throw error;
      await fetchCandidates();
    } catch (err) {
      console.error('Error deleting candidate:', err);
    }
  };

  const getCandidatesByStage = (stage: string) => {
    return candidates.filter(c => c.stage === stage);
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Recruitment Pipeline</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Candidate
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Box sx={{ overflowX: 'auto', pb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, minWidth: 'max-content' }}>
          {stages.map((stage) => (
            <Paper 
              key={stage.id} 
              sx={{ 
                width: 280, 
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 300px)',
                minHeight: 400
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {stage.label}
                </Typography>
                <Chip label={getCandidatesByStage(stage.id).length} size="small" />
              </Box>
              
              <Box sx={{ p: 1, overflowY: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {getCandidatesByStage(stage.id).map((candidate) => (
                  <Card key={candidate.id} sx={{ '&:hover': { boxShadow: 3 } }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">{candidate.name}</Typography>
                        <IconButton size="small" onClick={() => handleDelete(candidate.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <WorkIcon fontSize="inherit" /> {candidate.roleApplied}
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                        {candidate.email && (
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon fontSize="inherit" /> {candidate.email}
                          </Typography>
                        )}
                        {candidate.phone && (
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="inherit" /> {candidate.phone}
                          </Typography>
                        )}
                      </Box>

                      {stage.id !== 'hired' && stage.id !== 'rejected' && (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Move to"
                          value=""
                          onChange={(e) => handleStageChange(candidate.id, e.target.value)}
                        >
                          {stages.filter(s => s.id !== stage.id).map((s) => (
                            <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                          ))}
                        </TextField>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Candidate</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Full Name"
              fullWidth
              value={newCandidate.name || ''}
              onChange={(e) => setNewCandidate({ ...newCandidate, name: e.target.value })}
            />
            
            <TextField
              label="Role Applied For"
              fullWidth
              value={newCandidate.roleApplied || ''}
              onChange={(e) => setNewCandidate({ ...newCandidate, roleApplied: e.target.value })}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Email"
                  fullWidth
                  value={newCandidate.email || ''}
                  onChange={(e) => setNewCandidate({ ...newCandidate, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={newCandidate.phone || ''}
                  onChange={(e) => setNewCandidate({ ...newCandidate, phone: e.target.value })}
                />
              </Grid>
            </Grid>

            <TextField
              label="Interview Date"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newCandidate.interviewDate || ''}
              onChange={(e) => setNewCandidate({ ...newCandidate, interviewDate: e.target.value })}
            />

            <TextField
              label="Notes"
              multiline
              rows={3}
              fullWidth
              value={newCandidate.notes || ''}
              onChange={(e) => setNewCandidate({ ...newCandidate, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Add Candidate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecruitmentManagement;
